import { SSE_EVENT_TYPES } from './sseConstants';

/**
 * Parses SSE event data and returns structured object
 * @param {string} eventData - Raw event data string
 * @returns {object} Parsed event object
 */
export const parseSSEEvent = (eventData) => {
  try {
    if (!eventData || eventData.trim() === '') {
      return null;
    }

    // Try to parse as JSON
    const parsed = JSON.parse(eventData);
    
    // If it has a type field, it's already structured
    if (parsed.type) {
      return {
        type: parsed.type,
        data: parsed.data || parsed,
        timestamp: parsed.timestamp ? new Date(parsed.timestamp) : new Date(),
        correlationId: parsed.correlationId
      };
    }

    // Otherwise, return as-is with generic type
    return {
      type: 'unknown',
      data: parsed,
      timestamp: new Date()
    };
  } catch (error) {
    console.error('Error parsing SSE event:', error, 'Raw data:', eventData);
    return {
      type: 'error',
      data: { message: 'Failed to parse event', raw: eventData },
      timestamp: new Date()
    };
  }
};

/**
 * Checks if event data contains a specific type string
 * @param {string} eventData - Raw event data
 * @param {string} eventType - Event type to check for
 * @returns {boolean}
 */
export const isEventType = (eventData, eventType) => {
  try {
    if (!eventData) return false;
    
    // Check if the event data contains the type string
    if (eventData.includes(eventType)) {
      return true;
    }

    // Also try to parse and check the type field
    const parsed = parseSSEEvent(eventData);
    return parsed && parsed.type === eventType;
  } catch {
    return false;
  }
};

/**
 * Extracts specific data from SSE event
 * @param {string} eventData - Raw event data
 * @param {string} field - Field to extract
 * @returns {any} Extracted field value or null
 */
export const extractEventField = (eventData, field) => {
  try {
    const parsed = parseSSEEvent(eventData);
    if (!parsed) return null;

    // Check in data object first
    if (parsed.data && typeof parsed.data === 'object' && field in parsed.data) {
      return parsed.data[field];
    }

    // Check in root level
    if (field in parsed) {
      return parsed[field];
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Creates a type-safe event handler map
 * @returns {Map} Event handler map
 */
export const createEventHandlerMap = () => {
  return new Map();
};

/**
 * Registers an event handler
 * @param {Map} handlerMap - Handler map
 * @param {string|string[]} eventTypes - Event type(s) to handle
 * @param {Function} handler - Handler function
 */
export const registerEventHandler = (handlerMap, eventTypes, handler) => {
  const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
  
  types.forEach(type => {
    if (!handlerMap.has(type)) {
      handlerMap.set(type, []);
    }
    handlerMap.get(type).push(handler);
  });
};

/**
 * Dispatches an event to registered handlers
 * @param {Map} handlerMap - Handler map
 * @param {string} eventType - Event type
 * @param {any} eventData - Event data
 */
export const dispatchEvent = (handlerMap, eventType, eventData) => {
  const handlers = handlerMap.get(eventType);
  
  if (handlers && handlers.length > 0) {
    handlers.forEach(handler => {
      try {
        handler(eventData);
      } catch (error) {
        console.error(`Error in event handler for type ${eventType}:`, error);
      }
    });
  }
};

/**
 * Processes raw SSE message data and dispatches to appropriate handlers
 * @param {string} rawData - Raw SSE message data
 * @param {Map} handlerMap - Handler map
 */
export const processSSEMessage = (rawData, handlerMap) => {
  try {
    const event = parseSSEEvent(rawData);
    if (!event) return;

    // Dispatch to specific type handlers
    dispatchEvent(handlerMap, event.type, event.data);

    // Also dispatch to catch-all handler if registered
    dispatchEvent(handlerMap, '*', event);
  } catch (error) {
    console.error('Error processing SSE message:', error);
  }
};

/**
 * Validates SSE event structure
 * @param {object} event - Parsed event object
 * @returns {boolean}
 */
export const isValidSSEEvent = (event) => {
  return event && 
         typeof event === 'object' && 
         'type' in event && 
         'data' in event &&
         'timestamp' in event;
};

/**
 * Creates a formatted log message for SSE events
 * @param {string} eventType - Event type
 * @param {any} data - Event data
 * @param {string} level - Log level (info, warn, error)
 * @returns {string}
 */
export const createSSELogMessage = (eventType, data, level = 'info') => {
  const timestamp = new Date().toISOString();
  const dataStr = typeof data === 'object' ? JSON.stringify(data, null, 2) : data;
  return `[${timestamp}] [${level.toUpperCase()}] SSE Event: ${eventType}\nData: ${dataStr}`;
};

/**
 * Debounce function for SSE event handlers
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function}
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function for SSE event handlers
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit time in milliseconds
 * @returns {Function}
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};
